import React, { useState, useMemo, useCallback } from 'react';
import { isPeriodFullyCovered, anyOverlappingIntervals } from '../../../utils/schedules';
import moment from 'moment';
import { isEqual } from 'lodash';
import './DutyPlanner.css';
import Timeline from 'react-calendar-timeline';

import SkillIcon from '../../general/SkillIcon';
// make sure you include the timeline stylesheet or the timeline will not be styled
import 'react-calendar-timeline/lib/Timeline.css';
import { itemRenderer } from './timeline-helper';

const DutyPlannerTimeline = ({
  defaultFunctionId,
  startTime,
  endTime,
  readOnly,
  setContextMenuShow,
  storeInterval,
  dutyPlanner,
  functionsWithoutDefault,
  assignedUsersTimelineItems,
  skillsById,
  plannerMode
}) => {
  const [isDragging, setDragging] = useState(false);
  const [performableFunctionIds, setPerformableFunctionIds] = useState([]);

  const findItemById = id => assignedUsersTimelineItems.find(i => i.id === id);

  const storeIntervalAndResetDrag = (updatedInterval, updateStart, updateEnd) => {
    storeInterval(updatedInterval, updateStart, updateEnd);
    setDragging(false);
  };

  const onItemDrag = useCallback(
    ({ itemId }) => {
      const item = findItemById(itemId);
      if (!item) return;
      if (!item.skill_ids) return;

      const newIds = functionsWithoutDefault
        .filter(f => !f.skill_id || item.skill_ids.includes(f.skill_id))
        .map(f => f.id);

      setContextMenuShow(false);

      const ids = isEqual(performableFunctionIds, newIds) ? performableFunctionIds : newIds;
      setPerformableFunctionIds(ids);
      setDragging(true);
    },
    [functionsWithoutDefault, assignedUsersTimelineItems, performableFunctionIds]
  );

  // Returns the HTML class of the duty function on the left column of the duty
  // planner. It is green if it is fully covered, and orange if over-covered
  const getFunctionCoverClass = useCallback((dutyFunction, startTime, endTime, assignedUsersTimelineItems) => {
    // get the intervals for a function which are available (i.e. also
    // available for crewing)
    const intervals = assignedUsersTimelineItems.filter(
      i =>
        i.assigned_function_ids.includes(dutyFunction.id) &&
        (!i.availabilityCode || i.availabilityCode.available_for_crewing)
    );

    // True if the duty function has someone assigned for the entire
    // current viewing period
    const isCovered = isPeriodFullyCovered(intervals, startTime, endTime);
    // True if the duty function has more than one person covering it
    // for the same period during the viewing period
    const isOverCovered = anyOverlappingIntervals(intervals);

    if (isCovered && isOverCovered) return 'over-covered';
    if (isCovered) return 'fully-covered';
    return '';
  }, []);

  // Custom header for the function name on the left. Turns green when the
  // function is fully covered for the period
  const groupRenderer = ({ group }) => {
    const dutyFunction = group;

    const coveredClass = getFunctionCoverClass(
      dutyFunction,
      dutyPlanner.startTime,
      dutyPlanner.endTime,
      assignedUsersTimelineItems
    );

    const isPerformable = performableFunctionIds.includes(dutyFunction.id);
    const isPerformableClass = isPerformable ? 'performable' : '';
    const isDraggingClass = isDragging ? 'dragging' : '';

    const className = `function-header ${coveredClass} ${isPerformableClass} ${isDraggingClass}`;

    return (
      <div className={className}>
        <span className="title">
          {dutyFunction.title} <SkillIcon skill={skillsById[dutyFunction.skill_id]} />
        </span>
      </div>
    );
  };

  /**
   * Function to store the new intervals when an item is moved.
   * itemId is the id of the interval changed,
   * dragTime is the unix representation of the new moment in the timeline
   * in which the interval was modified
   * Start time will be the unix representation
   * of the point in the timeline.
   * We need to add the period length to it in order to
   * indicate the proper end time.
   * Then manage update for the membership intervals using membershipId.
   */
  const handleItemMove = (itemId, dragTime, newGroupIndex) => {
    const newFunction = functionsWithoutDefault[newGroupIndex];
    const item = findItemById(itemId);
    if (!item) return;
    if (!item.skill_ids) return;
    // Cannot perform the function => ignore changes
    if (newFunction.skill_id && !item.skill_ids.includes(newFunction.skill_id)) return;

    // calculate the new start & end time of the interval
    const periodLength = moment(item.end_time).diff(item.start_time);
    const start_time = moment(dragTime);
    const end_time = moment(dragTime).add(periodLength);

    // calculate the affected period of the schedule
    const updateStart = moment.min(item.start_time, start_time);
    const updateEnd = moment.max(item.end_time, end_time);
    const updatedInterval = {
      ...item,
      start_time,
      end_time,
      assigned_function_ids: [newFunction.id]
    };

    storeIntervalAndResetDrag(updatedInterval, updateStart, updateEnd);
  };

  /**
   * Function to store the new intervals when an item is resized.
   * - itemId is the id of the interval changed,
   * - time is the unix representation of the new moment in the timeline
   * in which the interval was modified
   * - Edge is the side of the interval which was picked to resize,
   * left end or right end
   * Conditionally decide based on the edge that was selected
   * to modify start or end time.
   * Then manage update for the membership intervals using membershipId.
   */
  const handleItemResize = (itemId, time, edge) => {
    const item = findItemById(itemId);
    if (!item) return;
    const start_time = edge === 'left' ? moment(time) : moment(item.start_time);
    const end_time = edge === 'left' ? moment(item.end_time) : moment(time);

    // calculate the affected period of the schedule
    const updateStart = moment.min(item.start_time, start_time);
    const updateEnd = moment.max(item.end_time, end_time);

    const updatedInterval = {
      ...item,
      start_time,
      end_time
    };

    storeIntervalAndResetDrag(updatedInterval, updateStart, updateEnd);
  };

  const deleteItemFromTimeline = itemId => {
    const item = findItemById(itemId);
    if (!item) return;

    if (plannerMode === 'duty') {
      storeIntervalAndResetDrag({
        ...item,
        assigned_function_ids: [defaultFunctionId]
      });
    } else {
      storeIntervalAndResetDrag({
        ...item,
        available: true,
        assigned_function_ids: []
      });
    }
  };

  // Function passed into the timeline for validating whether moving or resizing
  // intervals is allowed. It disallows dragging or resizing intervals:
  // - beyond the viewing period, to avoid users from accidentally editing
  //   duties outside the view.
  // - so that they overlap with other intervals
  const moveResizeValidator = (action, item, time, resizeEdge) => {
    // get all other intervals for same user
    const otherIntervals = assignedUsersTimelineItems.filter(
      interval => interval.membership_id === item.membership_id && interval.id !== item.id
    );

    let minStartTime = moment(dutyPlanner.startTime);
    let maxEndTime = moment(dutyPlanner.endTime);

    // interval is allowed to move between the immediate neighboring intervals,
    // or the interval boundaries, which ever is closest.
    for (const interval of otherIntervals) {
      if (item.start_time.isSameOrAfter(interval.end_time)) {
        // find the maximum end time to find the end time of the neighboring
        // interval
        minStartTime = moment.max(minStartTime, interval.end_time);
      }
      if (item.end_time.isSameOrBefore(interval.start_time)) {
        // find the minimum start time to find the start time of the neighboring
        // interval
        maxEndTime = moment.min(maxEndTime, interval.start_time);
      }
    }

    time = moment(time);
    if (resizeEdge === 'right' && time.isAfter(maxEndTime)) {
      // attempt to resize beyond end time. Return maxEndTime to disallow
      return maxEndTime.valueOf();
    }

    if (resizeEdge === 'right' && time.isBefore(item.start_time)) {
      // attempt to change end time to before start time of the interval
      // (causing negative length) Return item.start_time to disallow
      return item.start_time.valueOf();
    }

    if (resizeEdge === 'left' && time.isBefore(minStartTime)) {
      // attempt to resize before start time. Return minStartTime to disallow
      return minStartTime.valueOf();
    }

    if (resizeEdge === 'left' && time.isAfter(item.end_time)) {
      // attempt to change start time to after the end time of the interval
      // (causing negative length) Return item.end_time to disallow
      return item.end_time.valueOf();
    }

    if (action === 'move') {
      // Attempt to drag before start time. Return minStartTime to disallow
      if (time.isBefore(minStartTime)) return minStartTime.valueOf();

      const periodLength = item.end_time.diff(item.start_time);
      const intervalEndTime = time.clone().add(periodLength);

      // Attempt to drag beyond end time. Return maxEndTime - periodLength to
      // calculate the start time of the interval
      if (intervalEndTime.isAfter(maxEndTime)) {
        return maxEndTime.clone().subtract(periodLength).valueOf();
      }
    }

    return time.valueOf();
  };

  const intervalRightClick = (intervalId, e, time) => {
    setContextMenuShow(true, e.pageX, e.pageY, intervalId, time);
  };

  const augmentedItemsForTimeline = useMemo(() => {
    const items = assignedUsersTimelineItems;
    const settings = {
      canMove: !readOnly,
      canResize: readOnly ? false : 'both'
    };
    return items.map(item => ({ ...item, ...settings }));
  }, [assignedUsersTimelineItems]);

  // We prepare the props for the Timeline component. Some values depend on whether
  // The Duty Planner is readOnly or not.

  // First, we start with a set of base props that will never change, regardless of mode
  const visibleTimeStart = moment(startTime).valueOf();
  const visibleTimeEnd = moment(endTime).valueOf();

  // if either is not set, don't render (yet). If we do, we get an error:
  //
  // Error: You must provide either "defaultTimeStart" and "defaultTimeEnd" or
  // "visibleTimeStart" and "visibleTimeEnd" to initialize the Timeline
  if (!visibleTimeStart || !visibleTimeEnd) return null;

  const baseProps = {
    groups: functionsWithoutDefault,
    items: augmentedItemsForTimeline,
    visibleTimeStart,
    visibleTimeEnd,
    stackItems: true,
    groupRenderer: groupRenderer,
    onTimeChange: () => false,
    itemRenderer: itemRenderer
  };

  // If the mode is not readOnly, we set all the handlers
  const editProps = {
    canMove: true,
    canResize: 'both',
    onItemDrag: onItemDrag,
    onItemMove: handleItemMove,
    onItemResize: handleItemResize,
    onItemDoubleClick: deleteItemFromTimeline,
    moveResizeValidator: moveResizeValidator,
    onItemContextMenu: intervalRightClick
  };

  // If the mode is readOnly, we explicitly deny moving and resizing
  const readOnlyProps = {
    canMove: false,
    canResize: false,
    canChangeGroup: false
  };

  const props = readOnly ? { ...baseProps, ...readOnlyProps } : { ...baseProps, ...editProps };

  return <Timeline {...props} forceRerender={performableFunctionIds} />;
};

export default React.memo(DutyPlannerTimeline);
